// Libraries

import { DocumentIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { signAsset } from '@/api/assets';
// Assets
import { putInvoice } from '@/api/Sherpa/Draw';
import type { PatchInvoicePayload } from '@/api/Sherpa/Draw/types';
import { getTdmsByProjectPhase } from '@/api/tdm';
import Button from '@/components/UI/Button';
// custom components
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import HookMaskedInput from '@/components/UI/HookForm/Form/MaskedInput';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { DATE_FORMAT } from '@/constants';
import { SherpaProjectReactQueryKey, TdmReactQueryKey } from '@/constants/ReactQueryKeys';
import { useFileUpload } from '@/hooks/useFileUpload';
import { notifyUser } from '@/store/Toast';
import type { Deliverable } from '@/types/Constants';
import { byteConverter, formatDate2, formatDate3, getAcceptedFileTypes, sanitizeResponseError } from '@/utils';
import type { TFile } from '../../Documents/EditDocument';
import { Phases } from '../NewInvoices/constant';
import { validationSchema } from './validationSchema';

interface EditInvoiceProps {
  handleModal: () => void;
  projectName: string;
  invoice: any;
  deliverables?: any;
  milestones?: any;
}

interface EditInvoiceBasewProps extends EditInvoiceProps {
  invoiceRecentFiles: TFile[];
  setInvoiceRecentFiles: (value: TFile[]) => void;
}

const EditInvoiceBase = ({ handleModal, projectName, invoice, invoiceRecentFiles, setInvoiceRecentFiles }: EditInvoiceBasewProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { projectId, drawId, invoiceId } = useParams<string>();
  const transformDataRef = useRef<any>(null);

  const {
    metaData: { additionalDocs },
  } = invoice?.approval?.[invoice?.approval.length - 1] || { metaData: { additionalDocs: [] } };

  const [additionalDocRecentFiles, setAdditionalDocRecentFiles] = useState<TFile[]>(additionalDocs);

  const { upload } = useFileUpload({ signAsset: signAsset });

  const { watch, setValue } = useFormContext();
  const { phase } = watch();

  const fetchDeliverables = useQuery(
    [TdmReactQueryKey.GET_PROJECT_DELIVERABLES, projectId, phase?.value],
    () =>
      getTdmsByProjectPhase({
        type: 'deliverables',
        phase: phase?.value,
        page: 1,
        perPage: 10,
        projectId: Number(projectId),
      }),
    {
      enabled: !!phase, // This line ensures the query only runs when `invoice?.phaseSlug` is not null or undefined
    }
  );
  const deliverables = fetchDeliverables?.data?.data;

  useEffect(() => {
    if (deliverables?.length > 0 && invoice.deliverableId) {
      const deliverableId = deliverables.find((p: any) => p.id === invoice.deliverableId);
      setValue('deliverableId', deliverableId);
    } else {
      setValue('deliverableId', undefined);
    }
  }, [deliverables, setValue, invoice.deliverableId]);

  const fetchMilestones = useQuery(
    [TdmReactQueryKey.GET_PROJECT_MILESTONES, projectId, phase?.value],
    () =>
      getTdmsByProjectPhase({
        type: 'milestones',
        phase: phase?.value,
        page: 1,
        perPage: 10,
        projectId: Number(projectId),
      }),
    {
      enabled: !!phase, // This line ensures the query only runs when `invoice?.phaseSlug` is not null or undefined
    }
  );
  const milestones = fetchMilestones?.data?.data;

  useEffect(() => {
    if (milestones?.length > 0 && invoice.milestoneId) {
      const milestoneId = milestones.find((p: any) => p.id === invoice.milestoneId);
      setValue('milestoneId', milestoneId);
    } else {
      setValue('milestoneId', undefined);
    }
  }, [milestones, invoice.milestoneId, setValue]);

  const updateInvoice = useMutation(
    (data: PatchInvoicePayload) =>
      putInvoice({
        projectId: Number(projectId),
        drawId: Number(drawId),
        invoiceId: Number(invoiceId),
        data,
      }),
    {
      onSuccess: (_res) => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_INVOICE]);
        queryClient.invalidateQueries(['ProjectDrawInvoice-audit']);

        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Invoice updated successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: {
        key: string;
        type: string;
        resource: string;
      }[];
      files: File[];
      type: string;
    }) => upload(payload),
    {
      onSuccess: (response, { type }) => {
        const assetsFiles = response?.files.map((file: any, index: number) => {
          return {
            name: file.name,
            key: response?.sign.fKey ?? response.sign[index].fKey,
            contentType: file.type,
            size: file.size,
          };
        });
        if (type === 'invoice') {
          updateInvoice.mutate({
            ...transformDataRef.current,
            invoices: [...assetsFiles, ...(transformDataRef?.current?.invoices || [])],
          });
        }
        if (type === 'additionalDocs') {
          updateInvoice.mutate({
            ...transformDataRef.current,
            additionalDocs: [...assetsFiles, ...(transformDataRef?.current?.additionalDocs || [])],
          });
        }
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const updateInvoiceHandler = (data: any) => {
    const { invoiceDocuments, additionalDocuments } = data;
    const transformedInvoiceRecentFiles = invoiceRecentFiles.map(({ contentType, key, name, size }) => ({
      contentType,
      key,
      name,
      size,
    }));
    const transformedAdditionalDocRecentFiles = additionalDocRecentFiles.map(({ contentType, key, name, size }) => ({
      contentType,
      key,
      name,
      size,
    }));

    transformDataRef.current = {
      phaseSlug: data.phase?.value,
      amount: data?.amount,
      dueDate: formatDate2(data.dueDate),
      invoiceNumber: data.invoiceNumber,
      invoices: transformedInvoiceRecentFiles,
      additionalDocs: transformedAdditionalDocRecentFiles,
    };

    if (data?.deliverableId?.id) {
      transformDataRef.current.deliverableId = Number(data?.deliverableId.id);
    }

    if (data?.milestoneId?.id) {
      transformDataRef.current.milestoneId = Number(data?.milestoneId.id);
    }

    if (data?.notes) {
      transformDataRef.current.notes = data?.notes;
    }

    if (invoiceDocuments?.length || additionalDocuments?.length) {
      if (invoiceDocuments?.length) {
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];
        invoiceDocuments?.forEach((file: File) => {
          const acceptedFileTypes = getAcceptedFileTypes().split(',');
          if (acceptedFileTypes.includes(file.type)) {
            validFiles.push(file);
          } else {
            invalidFiles.push(file.name);
          }
        });
        if (invalidFiles.length > 0) {
          dispatch(
            notifyUser({
              showToast: true,
              toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
              toastType: 'failed',
            })
          );
        }
        if (validFiles.length > 0) {
          const signPayload = validFiles.map<{
            key: string;
            type: string;
            resource: string;
          }>((file) => ({
            key: file.name,
            type: file.type,
            resource: 'projectDrawInvoice',
          }));

          uploadFile.mutate({
            signAssetPayload: signPayload,
            files: validFiles,
            type: 'invoice',
          });
        }
      }
      if (additionalDocuments?.length) {
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];
        additionalDocuments?.forEach((file: File) => {
          const acceptedFileTypes = getAcceptedFileTypes().split(',');
          if (acceptedFileTypes.includes(file.type)) {
            validFiles.push(file);
          } else {
            invalidFiles.push(file.name);
          }
        });
        if (invalidFiles.length > 0) {
          dispatch(
            notifyUser({
              showToast: true,
              toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
              toastType: 'failed',
            })
          );
        }
        if (validFiles.length > 0) {
          const signPayload = validFiles.map<{
            key: string;
            type: string;
            resource: string;
          }>((file) => ({
            key: file.name,
            type: file.type,
            resource: 'projectDrawInvoice',
          }));

          uploadFile.mutate({
            signAssetPayload: signPayload,
            files: validFiles,
            type: 'additionalDocs',
          });
        }
      }
    } else {
      updateInvoice.mutate(transformDataRef.current);
    }
  };

  const handleInvoiceRemoveFiles = (file: TFile) => {
    const updatedFiles = invoiceRecentFiles.filter((f) => f.key !== file.key);
    setInvoiceRecentFiles(updatedFiles);
  };

  const handleAdditionalDocRemoveFiles = (file: TFile) => {
    const updatedFiles = additionalDocRecentFiles.filter((f) => f.key !== file.key);
    setAdditionalDocRecentFiles(updatedFiles);
  };

  return (
    <HookForm onSubmit={updateInvoiceHandler} className='space-y-2 grid grid-cols-2 gap-4'>
      <h3 className='text-sm col-span-2'>Project Name: {projectName}</h3>
      <HookReactSingleSelect componentClassName='col-span-1' label='Phase' name='phase' options={Phases} placeholder='' required />
      <HookInput name='invoiceNumber' type='text' label='Invoice Number*' />
      <HookMaskedInput name='amount' maskedComponent='currency' label='Invoice Amount*' />
      <HookInput name='dueDate' type='date' label='Due Date*' min={new Date()?.toISOString()?.substring(0, 10)} />
      <div className='col-span-2'>
        <HookFileInput label='Invoice*' name='invoiceDocuments' id='invoiceDocuments' multiple />
      </div>
      {invoiceRecentFiles && invoiceRecentFiles.length > 0 && (
        <div className='col-span-2 space-y-1'>
          <p>Recent Uploads</p>
          <ul>
            {invoiceRecentFiles.map((file) => (
              <li key={file.createdAt} className='flex justify-between items-center border rounded-md p-2'>
                <div className='flex gap-2 items-center'>
                  <DocumentIcon className='w-7 h-7 flex-none' />
                  <div className='flex flex-col'>
                    <h5 className='text-sm capitalize'>{file.name}</h5>
                    <div className='flex text-xs gap-5 text-neutralColor-normal'>
                      <span>Date: {dayjs(file.createdAt).format(DATE_FORMAT)}</span>
                      <span>Size: {byteConverter(file.size, 2)}</span>
                    </div>
                  </div>
                </div>
                <XCircleIcon
                  className='w-4 h-4 hover:text-tertiaryColor-normal cursor-pointer'
                  onClick={() => handleInvoiceRemoveFiles(file)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Deliverable'
        name='deliverableId'
        options={deliverables}
        getOptionLabel={(option) => (option as Deliverable).name}
        getOptionValue={(option) => (option as Deliverable).id.toString()}
        placeholder=''
      />
      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Milestone'
        name='milestoneId'
        options={milestones}
        getOptionLabel={(option) => (option as Deliverable).name}
        getOptionValue={(option) => (option as Deliverable).id.toString()}
        placeholder=''
      />
      <div className='col-span-2'>
        <HookFileInput label='Additional Documents' name='additionalDocuments' id='additionalDocuments' multiple />
      </div>
      {additionalDocRecentFiles && additionalDocRecentFiles.length > 0 && (
        <div className='col-span-2 space-y-1'>
          <p>Recent Uploads</p>
          <ul>
            {additionalDocRecentFiles.map((file) => (
              <li key={file.createdAt} className='flex justify-between items-center border rounded-md p-2'>
                <div className='flex gap-2 items-center'>
                  <DocumentIcon className='w-7 h-7 flex-none' />
                  <div className='flex flex-col'>
                    <h5 className='text-sm capitalize'>{file.name}</h5>
                    <div className='flex text-xs gap-5 text-neutralColor-normal'>
                      <span>Date: {dayjs(file.createdAt).format(DATE_FORMAT)}</span>
                      <span>Size: {byteConverter(file.size, 2)}</span>
                    </div>
                  </div>
                </div>
                <XCircleIcon
                  className='w-4 h-4 hover:text-tertiaryColor-normal cursor-pointer'
                  onClick={() => handleAdditionalDocRemoveFiles(file)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className='col-span-2'>
        <HookTextarea name='notes' label='Notes' rows={2} />
      </div>
      <div className='flex gap-4 mt-5 justify-end col-span-2'>
        <Button
          label='Cancel'
          type='button'
          btnStyle='btn-neutral'
          disabled={updateInvoice.isLoading || uploadFile.isLoading}
          onClick={handleModal}
        />
        <Button
          label={`${updateInvoice.isLoading || uploadFile.isLoading ? 'Updating...' : 'Edit Invoice'}`}
          type='submit'
          btnStyle='btn-primary'
          disabled={updateInvoice.isLoading || uploadFile.isLoading}
        />
      </div>
    </HookForm>
  );
};

export const EditInvoice = (props: EditInvoiceProps) => {
  const { invoice } = props;
  const {
    metaData: { invoices },
  } = invoice?.approval?.[invoice?.approval.length - 1] || { metaData: { invoices: [] } };

  const [invoiceRecentFiles, setInvoiceRecentFiles] = useState<TFile[]>(invoices);

  const formattedData = {
    dueDate: formatDate3(invoice.dueDate, 'yyyy-mm-dd'),
    notes: invoice.notes,
    amount: invoice.amount,
    invoiceNumber: invoice.invoiceNumber,
    phase: {
      label: Phases.find((p: any) => p.value === invoice.phaseSlug)?.label,
      value: Phases.find((p: any) => p.value === invoice.phaseSlug)?.value,
    },
  };

  return (
    <Modal>
      <h2 className='mt-0'>Edit Invoice</h2>
      <HookFormProvider defaultValues={formattedData} resolver={yupResolver(validationSchema(invoiceRecentFiles?.length || 0)) as any}>
        <EditInvoiceBase {...props} invoiceRecentFiles={invoiceRecentFiles} setInvoiceRecentFiles={setInvoiceRecentFiles} />
      </HookFormProvider>
    </Modal>
  );
};
